import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Eye, X, FileText, Download, Printer, RotateCcw, ChevronDown, PackagePlus, UserPlus, Receipt } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLabels } from '../hooks/useLabels';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { useTenant } from '../contexts/TenantContext';
import { useToast } from '../hooks/useToast';
import { api } from '../lib/api';
import GlassTable from '../components/GlassTable';
import Drawer from '../components/Drawer';
import { printThermalReceipt } from '../components/ThermalReceipt';

const INPUT = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition-colors';
const MODE_LABELS = { cash: 'Cash', card: 'Card', upi: 'UPI', wallet: 'Wallet', other: 'Other' };
const STATUS_STYLES = { paid:'bg-emerald-500/15 text-emerald-400 border-emerald-500/25', unpaid:'bg-red-500/15 text-red-400 border-red-500/25', partial:'bg-amber-500/15 text-amber-400 border-amber-500/25' };

export default function Sales() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const labels = useLabels();
  const { formatMoney, taxLabel, countryCode, taxRateDefault } = useTenantFormat();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewItem, setViewItem] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [pdfSaleId, setPdfSaleId] = useState(null);
  const [businessType, setBusinessType] = useState(null);
  const isRetail = businessType === 'retail' || businessType === 'supermarket';

  useEffect(() => {
    if (token) api.tenantMe(token).then(t => setBusinessType(t?.business_type || null)).catch(() => {});
  }, [token]);

  const handleReceipt = useCallback(async (saleOrId) => {
    try {
      const sale = typeof saleOrId === 'string' ? await api.saleGet(token, saleOrId) : saleOrId;
      const tenant = await api.tenantMe(token);
      printThermalReceipt(sale, tenant, formatMoney);
    } catch (e) { toast.show(e?.message || 'Failed to load sale for receipt', 'error'); }
  }, [token, formatMoney, toast]);

  const load = useCallback(async () => {
    if (!token) return;
    try { const r = await api.salesList(token); setItems(r.items||[]); }
    catch (e) { toast.show(e?.message||'Failed to load sales','error'); }
    finally { setLoading(false); }
  }, [token, toast]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(s => `${s.invoice_number||''} ${s.customer_name||''}`.toLowerCase().includes(q));
  }, [items, search]);

  const columns = [
    { key:'invoice_number', label:labels.invoice+' #', render:v=><span className="text-white font-semibold">{v||'—'}</span> },
    { key:'sale_date', label:'Date', render:v=><span className="text-white/70">{v?new Date(v).toLocaleDateString('en-IN'):'—'}</span> },
    { key:'customer_name', label:labels.customer, render:(v,row)=><div><span className="text-white/80">{v||'Walk-in'}</span>{row?.customer_phone && <span className="block text-white/40 text-[11px]">{row.customer_phone}</span>}</div> },
    { key:'total', label:'Total', align:'right', render:v=><span className="text-white font-bold">{formatMoney(v)}</span> },
    { key:'payment_status', label:'Status', align:'center', render:(v, row)=>(
      <div className="flex flex-col items-center gap-1">
        <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${STATUS_STYLES[v]||STATUS_STYLES.unpaid}`}>{v||'unpaid'}</span>
        {row?.total_qty_returned > 0 && (
          <span className="inline-block px-2 py-0.5 rounded-full text-[9px] font-bold bg-orange-500/15 text-orange-400 border border-orange-500/25" data-testid={`returned-chip-${row.id}`}>
            Returned: {row.total_qty_returned} of {(row.line_items || []).reduce((s, l) => s + (l.qty || 0), 0)}
          </span>
        )}
      </div>
    )},
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="sales-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight" data-testid="sales-title">{labels.sales}</h1>
          <p className="text-white/40 text-sm mt-1">{items.length} {labels.sales.toLowerCase()}</p>
        </div>
        <button data-testid="new-sale-btn" onClick={() => setNewOpen(true)}
          className="flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-bold hover:shadow-lg hover:shadow-teal-500/25 transition-all">
          <Plus size={18} /> New Sale
        </button>
      </div>
      <GlassTable testId="sales-table" columns={columns} data={filtered} loading={loading}
        emptyMessage={search?'No matching sales':`No ${labels.sales.toLowerCase()} yet`}
        emptyIcon={<ShoppingCart size={40} className="text-white/20" />}
        searchValue={search} onSearchChange={setSearch}
        searchPlaceholder={`Search ${labels.invoice.toLowerCase()} # or ${labels.customer.toLowerCase()}...`}
        actions={row => ( // eslint-disable-line react/no-unstable-nested-components
          <>
            <button data-testid={`view-sale-${row.id}`} onClick={e=>{e.stopPropagation();setViewItem(row);}} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="View details"><Eye size={16} /></button>
            {isRetail && (
              <button data-testid={`receipt-sale-${row.id}`} onClick={e=>{e.stopPropagation();handleReceipt(row);}} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-teal-600 transition-colors" title="Print Receipt"><Receipt size={16} /></button>
            )}
            <button data-testid={`pdf-sale-${row.id}`} onClick={e=>{e.stopPropagation();setPdfSaleId(row.id);}} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-brand-purple transition-colors" title="Preview Invoice PDF"><FileText size={16} /></button>
            <button data-testid={`cn-sale-${row.id}`} onClick={e=>{e.stopPropagation();navigate(`/notes/credit/new?sale_id=${row.id}`);}} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-orange-500 transition-colors" title="Create Credit Note"><RotateCcw size={16} /></button>
          </>
        )} />
      <ViewSaleDrawer open={!!viewItem} sale={viewItem} onClose={() => setViewItem(null)} labels={labels} formatMoney={formatMoney} taxLabel={taxLabel} countryCode={countryCode} isRetail={isRetail} onPrintReceipt={handleReceipt} />
      <NewSaleDrawer open={newOpen} onClose={() => setNewOpen(false)} token={token} onSuccess={(newSale) => { setNewOpen(false); load(); if (isRetail && newSale?.id) handleReceipt(newSale); }} labels={labels} formatMoney={formatMoney} taxLabel={taxLabel} countryCode={countryCode} taxRateDefault={taxRateDefault} />
      <InvoicePdfModal saleId={pdfSaleId} token={token} onClose={() => setPdfSaleId(null)} />
    </div>
  );
}

function ViewSaleDrawer({ open, sale, onClose, labels, formatMoney, taxLabel, countryCode, isRetail, onPrintReceipt }) {
  if (!sale) return null;
  const li = sale.line_items || [];
  const taxMode = sale.tax_mode_snapshot || (sale.vat ? 'VAT' : 'IN');
  const partyTaxIdLabel = sale.party_tax_id_label_snapshot || (taxMode === 'VAT' ? 'TRN' : 'GSTIN');
  let totalCgst = 0, totalSgst = 0, totalIgst = 0, totalVat = 0;
  let hasSnapshots = false;
  for (const l of li) {
    const tb = l.tax_breakdown_snapshot;
    if (tb) {
      hasSnapshots = true;
      totalCgst += tb.cgst || 0;
      totalSgst += tb.sgst || 0;
      totalIgst += tb.igst || 0;
      totalVat += tb.vat || 0;
    }
  }
  if (!hasSnapshots) {
    totalCgst = sale.cgst || 0;
    totalSgst = sale.sgst || 0;
    totalIgst = sale.igst || 0;
    totalVat = sale.vat || 0;
  }
  return (
    <Drawer open={open} onClose={onClose} title={`${labels.invoice} ${sale.invoice_number||''}`}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><p className="text-xs text-gray-500 uppercase font-semibold">Date</p><p className="text-gray-900 mt-1">{sale.sale_date?new Date(sale.sale_date).toLocaleDateString('en-IN'):'—'}</p></div>
          <div><p className="text-xs text-gray-500 uppercase font-semibold">{labels.customer}</p><p className="text-gray-900 mt-1">{sale.customer_name||'Walk-in'}</p></div>
          <div><p className="text-xs text-gray-500 uppercase font-semibold">Status</p><p className={`mt-1 font-bold capitalize ${sale.payment_status==='paid'?'text-emerald-600':sale.payment_status==='partial'?'text-amber-600':'text-red-500'}`}>{sale.payment_status||'unpaid'}</p></div>
          <div><p className="text-xs text-gray-500 uppercase font-semibold">Mode</p><p className="mt-1 font-semibold text-gray-800 capitalize">{sale.payment_mode ? MODE_LABELS[sale.payment_mode] || sale.payment_mode : '—'}</p></div>
          {sale.due_date && <div><p className="text-xs text-gray-500 uppercase font-semibold">Due Date</p><p className="text-gray-900 mt-1">{sale.due_date}</p></div>}
          {sale.place_of_supply && taxMode === 'IN' && <div><p className="text-xs text-gray-500 uppercase font-semibold">Place of Supply</p><p className="text-gray-900 mt-1">{sale.place_of_supply}</p></div>}
          {sale.customer_gstin && <div><p className="text-xs text-gray-500 uppercase font-semibold">{partyTaxIdLabel}</p><p className="text-gray-900 mt-1">{sale.customer_gstin}</p></div>}
        </div>
        {li.length>0 && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2" data-testid="sale-line-items">
            <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Line Items</p>
            {li.map((l,i) => {
              const tb = l.tax_breakdown_snapshot;
              return (
                <div key={i} className="py-2 border-b border-gray-100 last:border-0">
                  <div className="flex justify-between">
                    <div><p className="text-gray-900 text-sm font-medium">{l.product_name||l.name||'Item'}</p><p className="text-gray-400 text-xs">{l.qty} x {formatMoney(l.unit_price)}{tb ? ` · ${tb.tax_label || taxLabel} ${tb.tax_rate ?? l.gst_rate}%` : ` · ${taxLabel} ${l.gst_rate||0}%`}</p></div>
                    <p className="text-gray-900 font-semibold text-sm">{formatMoney(l.line_total || (l.qty||0)*(l.unit_price||0))}</p>
                  </div>
                  {tb && (
                    <div className="mt-1 text-[11px] text-gray-400">
                      {tb.mode === 'IN' ? (
                        tb.igst > 0
                          ? <span>IGST: {formatMoney(tb.igst)}</span>
                          : <span>CGST: {formatMoney(tb.cgst)} + SGST: {formatMoney(tb.sgst)}</span>
                      ) : (
                        <span>VAT: {formatMoney(tb.vat)}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2" data-testid="sale-tax-summary">
          <div className="flex justify-between"><span className="text-gray-500 text-sm">Subtotal</span><span className="text-gray-900 text-sm">{formatMoney(sale.subtotal)}</span></div>
          {taxMode === 'IN' ? (
            <>
              {(totalCgst > 0 || totalSgst > 0) && (
                <>
                  <div className="flex justify-between"><span className="text-gray-500 text-sm">CGST</span><span className="text-gray-900 text-sm" data-testid="sale-cgst">{formatMoney(totalCgst)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500 text-sm">SGST</span><span className="text-gray-900 text-sm" data-testid="sale-sgst">{formatMoney(totalSgst)}</span></div>
                </>
              )}
              {totalIgst > 0 && (
                <div className="flex justify-between"><span className="text-gray-500 text-sm">IGST</span><span className="text-gray-900 text-sm" data-testid="sale-igst">{formatMoney(totalIgst)}</span></div>
              )}
              {!totalCgst && !totalSgst && !totalIgst && (
                <div className="flex justify-between"><span className="text-gray-500 text-sm">GST</span><span className="text-gray-900 text-sm">{formatMoney(0)}</span></div>
              )}
            </>
          ) : (
            <div className="flex justify-between"><span className="text-gray-500 text-sm">VAT</span><span className="text-gray-900 text-sm" data-testid="sale-vat">{formatMoney(totalVat)}</span></div>
          )}
          <div className="flex justify-between border-t border-gray-200 pt-2 mt-1"><span className="text-gray-900 font-bold">Grand Total</span><span className="text-teal-600 font-extrabold text-lg" data-testid="sale-grand-total">{formatMoney(sale.total)}</span></div>
        </div>
        {sale.notes && <div><p className="text-xs text-gray-500 uppercase font-semibold">Notes</p><p className="text-gray-600 text-sm mt-1">{sale.notes}</p></div>}
        {isRetail && (
          <button data-testid="view-sale-print-receipt" onClick={() => onPrintReceipt(sale)}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-bold hover:shadow-lg hover:shadow-teal-500/25 transition-all">
            <Receipt size={16} /> Print Receipt
          </button>
        )}
      </div>
    </Drawer>
  );
}

function NewSaleDrawer({ open, onClose, token, onSuccess, labels, formatMoney, taxLabel, countryCode, taxRateDefault }) {
  const toast = useToast();
  const { profile } = useTenant();
  const isIN = countryCode === 'IN';
  const [tenantState, setTenantState] = useState('');

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [paymentMode, setPaymentMode] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [dueDate, setDueDate] = useState(() => { const d=new Date();d.setDate(d.getDate()+15);return d.toISOString().split('T')[0]; });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCust, setShowCust] = useState(false);
  const [showProd, setShowProd] = useState(false);
  const [custQ, setCustQ] = useState('');
  const [prodQ, setProdQ] = useState('');
  const [placeOfSupply, setPlaceOfSupply] = useState('');
  const [statesList, setStatesList] = useState([]);
  const [showPosPicker, setShowPosPicker] = useState(false);
  const [posQ, setPosQ] = useState('');
  const [quickAdd, setQuickAdd] = useState(false);
  const [quickAddCust, setQuickAddCust] = useState(false);
  const [debouncedProdQ, setDebouncedProdQ] = useState('');
  const prodDebounceRef = useRef(null);

  useEffect(() => {
    if (open && token) {
      api.customersList(token).then(r => setCustomers(r.items||[])).catch(() => {});
      api.productsList(token).then(r => setProducts(r.items||[])).catch(() => {});
      if (isIN) api.states().then(r => setStatesList(r.states||[])).catch(() => {});
      api.tenantMe(token).then(r => {
        const st = r?.state || '';
        setTenantState(st);
        setPlaceOfSupply(st);
      }).catch(() => {});
      setCustomerId(''); setCustomerName(''); setLineItems([]); setPaymentStatus('paid'); setPaymentMode('');
      setAmountPaid(''); setNotes(''); setCustQ(''); setProdQ('');
      setShowPosPicker(false); setPosQ(''); setQuickAdd(false); setQuickAddCust(false); setDebouncedProdQ('');
    }
  }, [open, token, isIN]);

  // Debounce product search query (250ms)
  const handleProdQChange = useCallback((val) => {
    setProdQ(val);
    if (prodDebounceRef.current) clearTimeout(prodDebounceRef.current);
    prodDebounceRef.current = setTimeout(() => setDebouncedProdQ(val), 250);
  }, []);

  const addProduct = p => { setLineItems(prev => [...prev,{product_id:p.id,name:p.name,qty:'1',unit_price:String(p.sell_price||0),gst_rate:p.gst_rate||18}]); setShowProd(false); setProdQ(''); setDebouncedProdQ(''); };

  const handleQuickAddSuccess = (newProd) => {
    setProducts(prev => [newProd, ...prev]);
    addProduct(newProd);
    setQuickAdd(false);
  };

  const sameState = !isIN || (placeOfSupply && tenantState && placeOfSupply.toLowerCase() === tenantState.toLowerCase());

  const totals = useMemo(() => {
    let sub=0,tax=0;
    for (const li of lineItems) { const line=(parseFloat(li.qty)||0)*(parseFloat(li.unit_price)||0); sub+=line; tax+=line*(li.gst_rate||0)/100; }
    return { subtotal:sub, tax, total:sub+tax };
  }, [lineItems]);

  // Client-side preview split (approximate — backend is authoritative)
  const taxPreview = useMemo(() => {
    if (!isIN) return { mode: 'VAT', vat: totals.tax, cgst: 0, sgst: 0, igst: 0 };
    if (sameState) return { mode: 'CGST_SGST', cgst: totals.tax / 2, sgst: totals.tax / 2, igst: 0, vat: 0 };
    return { mode: 'IGST', igst: totals.tax, cgst: 0, sgst: 0, vat: 0 };
  }, [isIN, sameState, totals.tax]);

  const submit = async () => {
    if (!customerId) { toast.show(`Pick a ${labels.customer.toLowerCase()}`,'error'); return; }
    if (!lineItems.length) { toast.show('Add at least one item','error'); return; }
    setSaving(true);
    try {
      const body = {
        customer_id: customerId,
        line_items: lineItems.map(li=>({product_id:li.product_id,qty:parseFloat(li.qty)||0,unit_price:parseFloat(li.unit_price)||0})),
        payment_status: paymentStatus,
        payment_mode: paymentMode || undefined,
        amount_paid: paymentStatus==='partial'?parseFloat(amountPaid||'0'):undefined,
        due_date: paymentStatus!=='paid'?dueDate:undefined,
        notes: notes||undefined,
        place_of_supply: isIN && placeOfSupply ? placeOfSupply : undefined,
      };
      const r = await api.salesCreate(token, body);
      toast.show(`Sale created · ${r.invoice_number||''}`,'success'); onSuccess(r);
    } catch (e) { toast.show(e?.message||'Create failed','error'); } finally { setSaving(false); }
  };

  const fCust = customers.filter(c => !custQ || c.name?.toLowerCase().includes(custQ.toLowerCase()));
  const fProd = products.filter(p => !debouncedProdQ || p.name?.toLowerCase().includes(debouncedProdQ.toLowerCase()));
  const fStates = statesList.filter(s => !posQ || s.toLowerCase().includes(posQ.toLowerCase()));

  return (
    <Drawer open={open} onClose={onClose} title="New Sale" wide>
      <div className="space-y-5">
        {/* Customer picker */}
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">{labels.customer}</p>
          <button data-testid="ns-pick-customer" onClick={() => { setShowCust(!showCust); setShowProd(false); setShowPosPicker(false); }}
            className={`w-full text-left px-4 py-3 rounded-xl border border-gray-200 text-sm ${customerName?'text-gray-900':'text-gray-400'} hover:border-gray-300 transition-colors`}>
            {customerName || `Select ${labels.customer.toLowerCase()}...`}
          </button>
          {showCust && (
            <div className="mt-2 bg-gray-50 rounded-xl border border-gray-200 p-3 max-h-48 overflow-y-auto space-y-1">
              <input data-testid="ns-cust-search" className={INPUT} placeholder="Search..." value={custQ} onChange={e=>setCustQ(e.target.value)} />
              {fCust.map(c => (<button key={c.id} data-testid={`ns-cust-${c.id}`} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-gray-700 text-sm transition-colors" onClick={()=>{setCustomerId(c.id);setCustomerName(c.name);setShowCust(false);}}>{c.name} <span className="text-gray-400 text-xs">{c.phone||''}</span></button>))}
              {fCust.length===0 && custQ.trim() && (
                <button data-testid="ns-quick-add-customer" onClick={() => setQuickAddCust(true)}
                  className="w-full text-left px-3 py-2.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-sm font-semibold transition-colors flex items-center gap-2">
                  <UserPlus size={16} className="text-indigo-500 flex-shrink-0" />
                  + Add new customer
                </button>
              )}
              {fCust.length===0 && !custQ.trim() && <p className="text-gray-400 text-sm py-2 text-center">No customers found</p>}
            </div>
          )}
          {quickAddCust && (
            <QuickAddCustomerModal
              prefillName={custQ.trim()}
              token={token}
              onSuccess={(created) => {
                setCustomers(prev => [created, ...prev]);
                setCustomerId(created.id);
                setCustomerName(created.name);
                setCustQ(created.name);
                setShowCust(false);
                setQuickAddCust(false);
              }}
              onClose={() => setQuickAddCust(false)}
            />
          )}
        </div>

        {/* Line items */}
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">Line Items</p>
          {lineItems.map((li,i) => (
            <div key={i} data-testid={`ns-line-${i}`} className="flex items-center gap-3 py-2 border-b border-gray-100">
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 text-sm font-medium truncate">{li.name}</p>
                <p className="text-gray-400 text-xs">{isIN ? 'GST' : 'VAT'} Rate {li.gst_rate}%</p>
              </div>
              <input data-testid={`ns-qty-${i}`} type="number" value={li.qty} onChange={e=>{const c=[...lineItems];c[i].qty=e.target.value;setLineItems(c);}} className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-gray-900 text-sm text-right outline-none focus:border-indigo-400" placeholder="Qty" />
              <input data-testid={`ns-price-${i}`} type="number" value={li.unit_price} onChange={e=>{const c=[...lineItems];c[i].unit_price=e.target.value;setLineItems(c);}} className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-gray-900 text-sm text-right outline-none focus:border-indigo-400" placeholder="₹" />
              <button onClick={()=>setLineItems(lineItems.filter((_,j)=>j!==i))} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"><X size={16} /></button>
            </div>
          ))}
          <button data-testid="ns-add-item" onClick={() => { setShowProd(!showProd); setShowCust(false); setShowPosPicker(false); }} className="mt-2 flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-50 border border-teal-200 text-teal-600 text-sm font-semibold hover:bg-teal-100 transition-colors"><Plus size={14} /> Add product</button>
          {showProd && (
            <div className="mt-2 bg-gray-50 rounded-xl border border-gray-200 p-3 max-h-48 overflow-y-auto space-y-1">
              <input data-testid="ns-prod-search" className={INPUT} placeholder="Search products..." value={prodQ} onChange={e=>handleProdQChange(e.target.value)} />
              {fProd.map(p => (<button key={p.id} data-testid={`ns-prod-${p.id}`} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-gray-700 text-sm transition-colors" onClick={()=>addProduct(p)}>{p.name} <span className="text-gray-400 text-xs">SKU {p.sku} · {formatMoney(p.sell_price)}</span></button>))}
              {fProd.length === 0 && debouncedProdQ.trim() && (
                <button data-testid="ns-quick-add-product" onClick={() => setQuickAdd(true)}
                  className="w-full text-left px-3 py-2.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-sm font-semibold transition-colors flex items-center gap-2">
                  <PackagePlus size={16} className="text-indigo-500 flex-shrink-0" />
                  + Add &lsquo;{debouncedProdQ.trim()}&rsquo; as new product
                </button>
              )}
              {fProd.length === 0 && !debouncedProdQ.trim() && <p className="text-gray-400 text-sm py-2 text-center">No products found</p>}
            </div>
          )}
          {quickAdd && (
            <QuickAddProductModal
              prefillName={debouncedProdQ.trim() || prodQ.trim()}
              token={token}
              isIN={isIN}
              formatMoney={formatMoney}
              onSuccess={handleQuickAddSuccess}
              onClose={() => setQuickAdd(false)}
            />
          )}
        </div>

        {/* Place of Supply — IN mode only */}
        {isIN && (
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">Place of Supply</p>
            <button data-testid="ns-place-of-supply" onClick={() => { setShowPosPicker(!showPosPicker); setShowCust(false); setShowProd(false); }}
              className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 hover:border-gray-300 transition-colors flex items-center justify-between">
              <span className={placeOfSupply ? 'text-gray-900' : 'text-gray-400'}>{placeOfSupply || 'Select state...'}</span>
              <ChevronDown size={14} className="text-gray-400" />
            </button>
            {placeOfSupply && tenantState && (
              <p className="text-gray-400 text-[11px] mt-1.5" data-testid="ns-pos-hint">
                {sameState
                  ? <span>Same as business state (<span className="text-teal-600">{tenantState}</span>) — intrastate CGST + SGST</span>
                  : <span>Different from business state (<span className="text-amber-600">{tenantState}</span>) — interstate IGST</span>
                }
              </p>
            )}
            {showPosPicker && (
              <div className="mt-2 bg-gray-50 rounded-xl border border-gray-200 p-3 max-h-48 overflow-y-auto space-y-1">
                <input data-testid="ns-pos-search" className={INPUT} placeholder="Search state..." value={posQ} onChange={e=>setPosQ(e.target.value)} />
                {fStates.map(s => (
                  <button key={s} data-testid={`ns-pos-${s}`} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${placeOfSupply===s ? 'bg-teal-50 text-teal-600 font-semibold' : 'hover:bg-gray-100 text-gray-700'}`}
                    onClick={()=>{setPlaceOfSupply(s);setShowPosPicker(false);setPosQ('');}}>
                    {s}
                  </button>
                ))}
                {fStates.length===0 && <p className="text-gray-400 text-sm py-2 text-center">No states found</p>}
              </div>
            )}
          </div>
        )}

        {/* Totals + tax preview */}
        {lineItems.length>0 && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2" data-testid="ns-totals-card">
            <div className="flex justify-between"><span className="text-gray-500 text-sm">Subtotal</span><span className="text-gray-900 text-sm">{formatMoney(totals.subtotal)}</span></div>
            {isIN ? (
              taxPreview.mode === 'CGST_SGST' ? (
                <>
                  <div className="flex justify-between"><span className="text-gray-500 text-sm">CGST</span><span className="text-gray-900 text-sm" data-testid="ns-preview-cgst">{formatMoney(taxPreview.cgst)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500 text-sm">SGST</span><span className="text-gray-900 text-sm" data-testid="ns-preview-sgst">{formatMoney(taxPreview.sgst)}</span></div>
                </>
              ) : (
                <div className="flex justify-between"><span className="text-gray-500 text-sm">IGST</span><span className="text-gray-900 text-sm" data-testid="ns-preview-igst">{formatMoney(taxPreview.igst)}</span></div>
              )
            ) : (
              <div className="flex justify-between"><span className="text-gray-500 text-sm">VAT</span><span className="text-gray-900 text-sm" data-testid="ns-preview-vat">{formatMoney(taxPreview.vat)}</span></div>
            )}
            <div className="flex justify-between border-t border-gray-200 pt-2"><span className="text-gray-900 font-bold">Total</span><span className="text-teal-600 font-extrabold text-lg" data-testid="ns-grand-total">{formatMoney(totals.total)}</span></div>
            <p className="text-gray-400 text-xs mt-2 pt-2 border-t border-gray-100" data-testid="ns-tax-caption">
              {isIN
                ? (sameState
                    ? <>Intrastate supply — <span className="text-gray-600 font-semibold">CGST + SGST</span> applied.</>
                    : <>Interstate supply — <span className="text-gray-600 font-semibold">IGST</span> applied.</>
                  )
                : <>Single-line <span className="text-gray-600 font-semibold">VAT @ {taxRateDefault ?? 5}%</span> — no interstate split in this region.</>
              }
            </p>
          </div>
        )}

        {/* Payment status */}
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">Payment Status</p>
          <div className="flex gap-2">
            {['paid','unpaid','partial'].map(s => (<button key={s} data-testid={`ns-status-${s}`} onClick={()=>setPaymentStatus(s)} className={`px-4 py-2 rounded-xl text-sm font-semibold capitalize transition-all ${paymentStatus===s?'bg-teal-500 text-white':'bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300'}`}>{s}</button>))}
          </div>
          {paymentStatus==='partial' && <label className="block mt-3" htmlFor="ns-amt"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount Paid</span><input id="ns-amt" data-testid="ns-amount-paid" type="number" className={`mt-1.5 ${INPUT}`} value={amountPaid} onChange={e=>setAmountPaid(e.target.value)} placeholder="₹" /></label>}
          {paymentStatus!=='paid' && <label className="block mt-3" htmlFor="ns-due"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</span><input id="ns-due" data-testid="ns-due-date" type="date" className={`mt-1.5 ${INPUT}`} value={dueDate} onChange={e=>setDueDate(e.target.value)} /></label>}
        </div>
        {/* Payment Mode */}
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">Payment Mode</p>
          <div className="flex flex-wrap gap-2" data-testid="ns-payment-mode-group">
            {[{v:'',label:'—'},{v:'cash',label:'💵 Cash'},{v:'card',label:'💳 Card'},{v:'upi',label:'📱 UPI'},{v:'wallet',label:'👛 Wallet'},{v:'other',label:'• Other'}].map(m => (
              <button key={m.v} data-testid={`ns-mode-${m.v||'none'}`} onClick={()=>setPaymentMode(m.v)}
                className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all ${paymentMode===m.v?'bg-indigo-500 text-white':'bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300'}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <label className="block" htmlFor="ns-notes"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes</span><textarea id="ns-notes" data-testid="ns-notes" className={`mt-1.5 ${INPUT} h-20 resize-none`} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Optional notes..." /></label>
        <button data-testid="ns-submit" onClick={submit} disabled={saving} className="w-full h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50">
          {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Create Sale'}
        </button>
      </div>
    </Drawer>
  );
}

/* ── Quick-Add Product Modal ── */
const UNIT_PRESETS = ['NOS', 'kg', 'L', 'pcs'];

function QuickAddCustomerModal({ prefillName, token, onSuccess, onClose }) {
  const toast = useToast();
  const [name, setName] = useState(prefillName || '');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setName(prefillName || ''); }, [prefillName]);

  const valid = name.trim().length >= 2 && phone.trim().length > 0;

  const submit = async () => {
    if (!valid) return;
    setSaving(true); setError('');
    try {
      const r = await api.customerCreate(token, { name: name.trim(), phone: phone.trim() });
      toast.show('Customer added', 'success');
      onSuccess(r);
    } catch (e) {
      setError(e?.message || 'Failed to create customer');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="quick-add-customer-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center"><UserPlus size={18} className="text-indigo-600" /></div>
            <h3 className="text-lg font-extrabold text-gray-900">Add Customer</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="qac-close"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <label className="block" htmlFor="qac-name">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Name *</span>
            <input id="qac-name" data-testid="qac-name" className={`mt-1.5 ${INPUT}`} value={name} onChange={e => setName(e.target.value)} placeholder="Customer name" />
          </label>
          <label className="block" htmlFor="qac-phone">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone *</span>
            <input id="qac-phone" data-testid="qac-phone" className={`mt-1.5 ${INPUT}`} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 90000 00000" />
          </label>
          {error && <p className="text-red-500 text-xs font-medium" data-testid="qac-error">{error}</p>}
        </div>
        <button data-testid="qac-save" onClick={submit} disabled={saving || !valid}
          className="w-full mt-5 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-indigo-500/25 transition-all disabled:opacity-50">
          {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Save Customer'}
        </button>
      </div>
    </div>
  );
}

function QuickAddProductModal({ prefillName, token, isIN, formatMoney, onSuccess, onClose }) {
  const toast = useToast();
  const [name, setName] = useState(prefillName || '');
  const [price, setPrice] = useState('');
  const [unit, setUnit] = useState('NOS');
  const [customUnit, setCustomUnit] = useState('');
  const [hsnCode, setHsnCode] = useState('');
  const [taxRate, setTaxRate] = useState('0');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setName(prefillName || ''); }, [prefillName]);

  const effectiveUnit = unit === 'Other' ? customUnit : unit;

  const submit = async () => {
    if (!name.trim()) { toast.show('Product name required', 'error'); return; }
    if (!price || parseFloat(price) <= 0) { toast.show('Price required', 'error'); return; }
    setSaving(true);
    try {
      const sku = name.trim().toUpperCase().replace(/[^A-Z0-9]/g, '-').slice(0, 12) + '-' + Math.random().toString(36).slice(2, 6).toUpperCase();
      const body = {
        name: name.trim(),
        sku,
        sell_price: parseFloat(price),
        unit: effectiveUnit || 'NOS',
        hsn_code: hsnCode || undefined,
        gst_rate: taxRate ? parseFloat(taxRate) : 0,
        stock_qty: 0,
        is_active: true,
      };
      const r = await api.productsCreate(token, body);
      toast.show('Product added', 'success');
      onSuccess(r);
    } catch (e) {
      toast.show(e?.message || 'Failed to create product', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="quick-add-product-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center"><PackagePlus size={18} className="text-indigo-600" /></div>
            <h3 className="text-lg font-extrabold text-gray-900">Quick Add Product</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="quick-add-close"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <label className="block" htmlFor="qa-name">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Name *</span>
            <input id="qa-name" data-testid="qa-name" className={`mt-1.5 ${INPUT}`} value={name} onChange={e => setName(e.target.value)} placeholder="Product name" />
          </label>
          <label className="block" htmlFor="qa-price">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Price *</span>
            <input id="qa-price" data-testid="qa-price" type="number" className={`mt-1.5 ${INPUT}`} value={price} onChange={e => setPrice(e.target.value)} placeholder="0" />
          </label>
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">Unit</span>
            <div className="flex flex-wrap gap-2">
              {UNIT_PRESETS.map(u => (
                <button key={u} data-testid={`qa-unit-${u}`} onClick={() => setUnit(u)}
                  className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-all ${unit === u ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{u}</button>
              ))}
              <button data-testid="qa-unit-Other" onClick={() => setUnit('Other')}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-all ${unit === 'Other' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Other</button>
            </div>
            {unit === 'Other' && (
              <input data-testid="qa-unit-custom" className={`mt-2 ${INPUT}`} value={customUnit} onChange={e => setCustomUnit(e.target.value)} placeholder="e.g. box, pair, set" />
            )}
          </div>
          {isIN && (
            <label className="block" htmlFor="qa-hsn">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">HSN Code</span>
              <input id="qa-hsn" data-testid="qa-hsn" className={`mt-1.5 ${INPUT}`} value={hsnCode} onChange={e => setHsnCode(e.target.value)} placeholder="Optional" />
            </label>
          )}
          <label className="block" htmlFor="qa-tax">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tax Rate %</span>
            <input id="qa-tax" data-testid="qa-tax" type="number" className={`mt-1.5 ${INPUT}`} value={taxRate} onChange={e => setTaxRate(e.target.value)} placeholder="0" />
          </label>
        </div>
        <button data-testid="qa-save" onClick={submit} disabled={saving}
          className="w-full mt-5 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-indigo-500/25 transition-all disabled:opacity-50">
          {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Save Product'}
        </button>
      </div>
    </div>
  );
}

/* ── Invoice PDF Preview Modal ── */
function InvoicePdfModal({ saleId, token, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const BASE = process.env.REACT_APP_BACKEND_URL || '';
  const pdfApiUrl = `${BASE}/api/sales/${saleId}/invoice.pdf`;

  useEffect(() => {
    if (!saleId || !token) return;
    let cancelled = false;
    setLoading(true); setError(null); setBlobUrl(null);
    fetch(pdfApiUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error('Failed to load PDF'); return r.blob(); })
      .then(blob => { if (!cancelled) setBlobUrl(URL.createObjectURL(blob)); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [saleId, token, pdfApiUrl]);

  // Cleanup blob URL on unmount
  useEffect(() => { return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }; }, [blobUrl]);

  if (!saleId) return null;

  const handleDownload = () => {
    if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl; a.download = `invoice-${saleId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    }
  };

  const handlePrint = () => {
    const iframe = document.getElementById('invoice-pdf-iframe');
    if (iframe?.contentWindow) { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="invoice-pdf-modal">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-200 w-[90vw] max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-gray-900 font-bold text-lg" data-testid="invoice-pdf-title">Invoice Preview</h3>
          <div className="flex items-center gap-2">
            <button data-testid="invoice-pdf-download" onClick={handleDownload} disabled={!blobUrl}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors disabled:opacity-40">
              <Download size={14} /> Download
            </button>
            <button data-testid="invoice-pdf-print" onClick={handlePrint} disabled={!blobUrl}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-purple text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40">
              <Printer size={14} /> Print
            </button>
            <button data-testid="invoice-pdf-close" onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-gray-50 flex items-center justify-center">
          {loading && <div className="w-6 h-6 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" />}
          {error && <p className="text-red-500 text-sm">{error}</p>}
          {blobUrl && (
            <iframe
              id="invoice-pdf-iframe"
              title="Invoice PDF"
              src={blobUrl}
              className="w-full h-full border-0"
              data-testid="invoice-pdf-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
}

